import React, { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { Loader2, X, ImageIcon } from "lucide-react";

// Fetches auth-protected screenshots as blob URLs; only loads paths not yet loaded.
export function useScreenshotPreviews(screenshots) {
  const [previewUrls, setPreviewUrls] = useState({});
  const inflight = useRef(new Set());

  useEffect(() => {
    const pending = screenshots.filter((s) => !previewUrls[s.path] && !inflight.current.has(s.path));
    if (!pending.length) return;
    pending.forEach((s) => inflight.current.add(s.path));
    (async () => {
      const urls = {};
      for (const s of pending) {
        try {
          const res = await api.get(`/files/${s.path}`, { responseType: "blob" });
          urls[s.path] = URL.createObjectURL(res.data);
        } catch (_e) {
          // leave missing; placeholder spinner stays and a later open retries
        } finally {
          inflight.current.delete(s.path);
        }
      }
      if (Object.keys(urls).length) setPreviewUrls((p) => ({ ...p, ...urls }));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenshots]);

  return previewUrls;
}

export default function ScreenshotGrid({ screenshots, onRemove }) {
  const previewUrls = useScreenshotPreviews(screenshots);
  if (screenshots.length === 0) {
    return (
      <div className="mt-2 border border-dashed border-[#27272A] p-6 text-center text-[#71717A] text-xs rounded-sm">
        <ImageIcon className="h-5 w-5 mx-auto mb-2 opacity-50" />
        No screenshots yet
      </div>
    );
  }
  return (
    <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="screenshots-grid">
      {screenshots.map((s) => (
        <div key={s.path} className="relative group border border-[#27272A] rounded-sm overflow-hidden bg-[#08080A]" data-testid={`screenshot-${s.path}`}>
          {previewUrls[s.path] ? (
            <img src={previewUrls[s.path]} alt={s.name} className="w-full h-32 object-cover" />
          ) : (
            <div className="w-full h-32 flex items-center justify-center text-[#71717A]"><Loader2 className="h-4 w-4 animate-spin" /></div>
          )}
          <button type="button" onClick={() => onRemove(s.path)} data-testid={`remove-screenshot-${s.path}`}
            className="absolute top-1 right-1 h-6 w-6 bg-black/70 text-white rounded-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#FF3B30]">
            <X className="h-3 w-3" />
          </button>
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-2 py-1 text-[10px] text-white truncate font-mono">{s.name}</div>
        </div>
      ))}
    </div>
  );
}
